import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

async function fetchServerVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data: { version?: string } = await res.json();
    return data.version ?? null;
  } catch {
    return null;
  }
}

export function useAppVersion() {
  const initialVersion = useRef<string | null>(null);
  const [hasNewVersion, setHasNewVersion] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    async function check() {
      const version = await fetchServerVersion();
      if (cancelled || !version) return;

      if (initialVersion.current === null) {
        initialVersion.current = version;
      } else if (version !== initialVersion.current) {
        setHasNewVersion(true);
        return;
      }

      timeoutId = setTimeout(check, POLL_INTERVAL_MS);
    }

    check();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  async function applyUpdate() {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/' && currentPath !== '/login') {
      localStorage.setItem('redirect_url', currentPath);
    }

    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }

    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }

    window.location.reload();
  }

  return { hasNewVersion, applyUpdate };
}
